


































































































































































































import moment from 'moment';

import { mapActions, mapGetters } from 'vuex';
import { ErrorManager } from '@/models/error';
import { AxiosError } from 'axios';

import { merge, omit } from 'lodash';
import auth from '@/mixins/auth';

import {
  EmployeeOrgChart,
  EmployeeNode,
  EmployeeType,
  IEmployee
} from '@/models';

import Functions from '@/mixins/functions';
import mixins from '@/utils/mixins';
import {
  EmployeeFilterParams,
  IEmployeeFetchActionPayload,
  IEmployeeNonPaginatedFetchActionPayload
} from '@/store/employee';

import EmployeeDetailCreateFormDialog from '@/components/employee/EmployeeDetailCreateFormDialog.vue';
import EmployeeDetailProfileFormDialog from '@/components/employee/EmployeeDetailProfileFormDialog.vue';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import EmployeeSearchFilter from '@/components/employee/EmployeeSearchFilter.vue';
import EmployeeViewSelector, {
  ViewType
} from '@/components/employee/EmployeeViewSelector.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import EmployeeExportYearFormDialog from '@/components/employee/EmployeeExportYearFormDialog.vue';
import ALPage from '@/components/core/Page/ALPage.vue';
import EmployeeOrgizationChart from '@/components/employee/EmployeeOrgizationChart.vue';
import EmployeeList from '@/components/employee/EmployeeList.vue';
import EmployeeUploadFormDialog from '@/components/employee/EmployeeUploadFormDialog.vue';

const baseMixins = mixins(auth, Functions);

interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    employeeDetailCreateFormDialog: InstanceType<
      typeof EmployeeDetailCreateFormDialog
    >;

    employeeDetailProfileFormDialog: InstanceType<
      typeof EmployeeDetailProfileFormDialog
    >;

    employeeUploadFormDialog: InstanceType<typeof EmployeeUploadFormDialog>;

    employeeExportYearFormDialog: InstanceType<
      typeof EmployeeExportYearFormDialog
    >;
  };
}

export default baseMixins
  .extend<Options>({ functional: false })
  .extend({
    name: 'EmployeeListPage',

    components: {
      EmployeeDetailCreateFormDialog,
      ErrorAlert,
      ConfirmationDialog,
      EmployeeSearchFilter,
      EmployeeViewSelector,
      EmployeeOrgizationChart,
      EmployeeList,
      EmployeeDetailProfileFormDialog,
      EmployeeUploadFormDialog,
      EmployeeExportYearFormDialog,
      ALPage
    },

    data() {
      return {
        view: ViewType.List,

        loading: false,
        deleteDialog: false,

        selectedEmployee: null as IEmployee | null,

        errorMessage: '',
        detailErrorMessage: '',

        deleteErrorMessage: '',
        deleteDetailErrorMessage: '',

        isSearchFilterActive: false,

        params: {
          search: this.$route.query.search as string,
          page: Number(this.$route.query.page ?? 1)
        } as IEmployeeFetchActionPayload & EmployeeFilterParams
      };
    },

    computed: {
      ...mapGetters({
        getEmployeeById: 'employee/getEmployeeById',
        employees: 'employee/getEmployees',
        numEmployees: 'employee/getEmployeeCount'
      }),

      employeeTrees(): EmployeeNode[] {
        return new EmployeeOrgChart(this.employees).trees;
      },

      extraFiltersApplied(): boolean {
        return Object.keys(omit(this.$route.query, ['page', 'search'])).some(
          v => !!v
        );
      },

      currentViewComponent(): string {
        switch (this.view) {
          case ViewType.Chart:
            return 'EmployeeOrgizationChart';
          case ViewType.List:
          default:
            return 'EmployeeList';
        }
      },
      isChartView(): boolean {
        return this.view == ViewType.Chart;
      },

      employeeIdQuery(): number {
        return Number(this.$route.query.id);
      },

      canEditEmployee(): boolean {
        return this.userHasRoles(['HR Personnel']);
      }
    },

    watch: {
      'params.search'(search: string) {
        this.debounceFetchEmployees({
          ...this.params,
          search: search,
          page: 1
        }).then(() => {
          this.params.page = 1;
        });
      },

      view: {
        handler() {
          // update `this.params` from the route query object.
          // omitting `search` and `page` as they are already set in the data vue option
          merge(this.params, omit(this.$route.query, ['page', 'search']));

          this.loading = true;

          this.searchEmployees(this.params);
        }
      }
    },

    async mounted() {
      // update `this.params` from the route query object.
      // omitting `search` and `page` as they are already set in the data vue option
      merge(this.params, omit(this.$route.query, ['page', 'search']));

      this.loading = true;

      try {
        await this.searchEmployees(this.params);

        if (this.employeeIdQuery && this.canEditEmployee) {
          const employee = await this.getEmployeeById(this.employeeIdQuery);
          if (employee) {
            this.openEditEmployeeDialog(employee);
          }
        }
      } catch (error) {
        this.errorMessage = ErrorManager.extractApiError(error as AxiosError);
      }
    },

    methods: {
      moment,

      ...mapActions({
        fetchEmployees: 'employee/fetchEmployees',
        deleteEmployee: 'employee/deleteEmployee'
      }),

      openYearSelectionDialog() {
        this.$refs.employeeExportYearFormDialog.open();
      },

      openCreateSalaryEmployeeDialog() {
        this.$refs.employeeDetailCreateFormDialog.open(EmployeeType.Salary);
      },

      openCreateHourlyEmployeeDialog() {
        this.$refs.employeeDetailCreateFormDialog.open(EmployeeType.Hourly);
      },

      openEditEmployeeDialog(employee: IEmployee) {
        this.$refs.employeeDetailProfileFormDialog.open(employee);
      },

      openEmployeeUploadFormDialog() {
        this.$refs.employeeUploadFormDialog.open();
      },

      openEmployeeDeleteDialog(employee: IEmployee) {
        this.selectedEmployee = employee;
        this.deleteDialog = true;
      },

      cancelEmployeeDeleteDialog() {
        this.selectedEmployee = null;
        this.deleteDialog = false;
      },

      clearAllErrors(): void {
        this.errorMessage = '';
        this.detailErrorMessage = '';

        this.deleteErrorMessage = '';
        this.deleteDetailErrorMessage = '';
      },

      async removeEmployee(employee: IEmployee) {
        this.clearAllErrors();
        this.loading = true;

        try {
          await this.deleteEmployee(employee.id);

          this.deleteDialog = false;
        } catch (err) {
          const error = err as AxiosError;
          this.deleteErrorMessage = ErrorManager.extractApiError(error);
          if (error.response?.data.detail) {
            this.deleteDetailErrorMessage = error.response.data.detail;
          }
        } finally {
          this.loading = false;
        }
      },

      async debounceFetchEmployees(params: IEmployeeFetchActionPayload) {
        this.clearAllErrors();
        this.loading = true;

        return this.debounce(this.searchEmployees, 1000)(params);
      },

      searchEmployees(params: IEmployeeFetchActionPayload) {
        /* this will update the URL query based on param values. */
        this.$router
          .replace({
            name: 'employee-list',
            query: params as Record<string, any>
          })
          .catch((e: Error) => e); //catch navigation duplication error

        const queryParams:
          | IEmployeeFetchActionPayload
          | IEmployeeNonPaginatedFetchActionPayload = this.isChartView
          ? {
              ...omit(params, ['page']),
              type: EmployeeType.Salary,
              is_active: 'true'
            }
          : params;

        return this.fetchEmployees(queryParams)
          .catch(error => {
            this.errorMessage = ErrorManager.extractApiError(error);
          })
          .finally(() => {
            this.loading = false;
          });
      },

      refreshSearchFilter(params: EmployeeFilterParams) {
        this.clearAllErrors();

        this.loading = true;
        this.searchEmployees({
          page: 1,
          search: this.params.search,
          ...params
        }).then(() => {
          // update params object
          this.params.type = params.type;
          this.params.is_active = params.is_active;
          this.params.hr_department = params.hr_department;
          this.params.supervisor = params.supervisor;
          this.params.page = 1;
        });
      },

      rebuild() {
        this.loading = true;

        this.searchEmployees(this.params);
      },

      onUpdatePage(newPage: number) {
        if (newPage != this.params.page) {
          this.searchEmployees({
            ...this.params,
            page: newPage
          }).then(() => {
            this.params.page = newPage;
          });
        }
      }
    }
  });
